import { Box, Flex, Text } from '@chakra-ui/react';
import { BaseAvatar, BaseIcon, BaseTag, BaseTooltip, SelectOption, useStandardToast } from '@cks-internal/react-base';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { useSavePrimaryTravellerIdMutation } from '../../../features/activities/editActivity/travellerList/api/useSavePrimaryTravellerIdMutation';
import { useActivityQueryInvalidator } from '../../../features/activities/editActivity/useActivityQueryInvalidator';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../constants/queryKeys';
import { ActivityEndpoints } from '../../../constants/endpoints/activityEndpoints.ts';
import { CompanyContactType, companyContactTypeLabels } from '../../../enums/crm/CompanyContactType';

type Props = {
  selectOption: SelectOption;
  activityId?: number;
  disableClick?: boolean;
  showContactType?: boolean;
};

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const SelectedTravellerPill = (props: Props) => {
  const isPrimary = props.selectOption.Metadata?.find((x) => x.Key === 'isPrimary')?.Value === true;
  const name = props.selectOption.Label;

  const rawContactType = props.selectOption.Metadata?.find((x) => x.Key === 'companyContactType')?.Value;
  const contactTypeLabel =
    props.showContactType && typeof rawContactType === 'number'
      ? companyContactTypeLabels[rawContactType as CompanyContactType]
      : undefined;

  const toast = useStandardToast();
  const savePrimaryTravellerIdMutation = useSavePrimaryTravellerIdMutation();
  const queryClient = useQueryClient();
  const queryInvalidator = useActivityQueryInvalidator(props.activityId ?? 0);

  const handleMakePrimary = async () => {
    if (!isPrimary && typeof props.selectOption.Value !== 'boolean' && props.activityId && !props.disableClick) {
      try {
        await savePrimaryTravellerIdMutation.mutateAsync({
          activityId: props.activityId,
          primaryTravellerId: Number(props.selectOption.Value)
        });

        // Invalidate relevant queries to update the UI
        await queryClient.invalidateQueries({
          queryKey: ['useLoadTravellerListAndPaymentInfoQuery'],
          type: 'all'
        });

        // Invalidate activity status query to update traveler select options
        await queryInvalidator.invalidateStatusQuery();

        // Invalidate edit activity model query to update InitialTravellerSelectOptions
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.editActivity(props.activityId), ActivityEndpoints.edit.get(props.activityId)],
          type: 'all'
        });

        toast.successToast('Success', 'Primary Traveler Updated');
      } catch {
        toast.errorToast('Error', 'Failed to update primary traveler');
      }
    }
  };

  return (
    <Flex alignItems='center' justifyContent='center' gap={'4px'} padding={'0 4px'}>
      {isPrimary ? (
        <BaseTooltip content='Primary Traveller'>
          <Box
            position='relative'
            width='24px'
            height='24px'
            display='flex'
            alignItems='center'
            justifyContent='center'
            cursor='default'>
            <BaseIcon icon={faStar} color='orange.500' fontSize='2xl' />
            <Text position='absolute' fontSize='2xs' fontWeight={600} color='black' lineHeight='1'>
              {typeof name === 'string' ? getInitials(name) : ''}
            </Text>
          </Box>
        </BaseTooltip>
      ) : (
        <BaseTooltip content={props.disableClick ? 'Primary Traveller' : 'Make Primary'}>
          <Box cursor={props.activityId && !props.disableClick ? 'pointer' : 'default'} onClick={handleMakePrimary}>
            <BaseAvatar size={'2xs'} disableTooltip name={typeof name === 'string' ? name : ''} />
          </Box>
        </BaseTooltip>
      )}
      <Box fontSize={'sm'}>{name}</Box>
      {contactTypeLabel && (
        <BaseTag variant={'subtle'} size={'sm'}>
          {contactTypeLabel}
        </BaseTag>
      )}
    </Flex>
  );
};
